import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { devLog } from '../utils/envUtils';

/**
 * Hook personnalisé pour gérer la soumission des formulaires d'authentification
 * @returns {Object} Fonctions pour gérer l'authentification
 */
export const useAuthSubmission = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { actions } = useApp();

  /**
   * Gère la soumission du formulaire d'inscription
   */
  const handleRegistration = useCallback(async (submissionData) => {
    await actions.register(submissionData);
    // Déclencher l'onboarding et rediriger vers le dashboard
    actions.showOnboarding();
    navigate('/');
  }, [actions, navigate]);

  /**
   * Gère la soumission du formulaire de connexion
   */
  const handleLogin = useCallback(async (submissionData) => {
    await actions.login(submissionData);
    // Rediriger vers la page d'origine ou l'accueil
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  }, [actions, navigate, location]);

  /**
   * Détermine le type d'erreur pour une meilleure gestion
   */
  const categorizeError = (error) => {
    const errorMessage = error.message || '';
    const errorStatus = error.status || error.response?.status;
    
    // Erreurs réseau
    if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorStatus >= 500) {
      return { type: 'network' };
    }
    
    // Erreurs d'authentification (401, 403)
    if (errorStatus === 401 || errorStatus === 403 || errorMessage.includes('authentification')) {
      return { type: 'auth' };
    }
    
    // Erreurs de validation (400)
    if (errorStatus === 400) {
      return { type: 'validation' };
    }
    
    // Autre erreur
    return { type: 'unknown' };
  };

  /**
   * Gère la soumission complète du formulaire
   */
  const submitAuth = useCallback(async (isRegistering, submissionData) => {
    try {
      if (isRegistering) {
        await handleRegistration(submissionData);
      } else {
        await handleLogin(submissionData);
      }
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur auth:', error);
      
      const errorCategory = categorizeError(error);
      
      return { 
        success: false, 
        error, 
        errorType: errorCategory.type
      };
    }
  }, [handleRegistration, handleLogin]);

  /**
   * Vérifie si l'utilisateur doit être redirigé (déjà connecté)
   */
  const checkAuthRedirect = useCallback((isAuthenticated) => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
      return true;
    }
    return false;
  }, [navigate, location]);

  return {
    submitAuth,
    handleRegistration,
    handleLogin,
    checkAuthRedirect
  };
};
